/**
 * Certification-gap scenes (f-gases, hot-works-bitumen,
 * pressure-vessels-awareness) drawn as technical safety diagrams on the shared
 * `<Blueprint>` stage (scenes-shared.tsx): a room section, a roof-edge detail
 * and a vessel with its safety fittings. Each of the two steps is a complete
 * static frame in `<On>`; only subtle ambient motion (waft, pour, clock hand)
 * runs on top.
 */

import { Blueprint, blueprintIds, On, Solid, Verdict } from './scenes-shared';

/** F-gas leak, room section: refrigerant sinks from the split unit and pools at the floor ✗ vs. certified recovery into a service cylinder ✓. */
export function RefrigerantLeakScene({ en }: { en: boolean }) {
  const ids = blueprintIds('refrigerantLeak');
  return (
    <Blueprint
      id="refrigerantLeak"
      label={
        en
          ? 'Refrigerant leak: floor pooling vs. certified recovery'
          : 'Únik chladiva: hromadění u podlahy vs. certifikované odsátí'
      }
      title={en ? 'Refrigerant leak · room section' : 'Únik chladiva · řez místností'}
      source={
        en
          ? 'Regulation (EU) 2024/573 · Act 73/2012 Coll.'
          : 'nařízení (EU) 2024/573 · z. 73/2012 Sb.'
      }
    >
      {/* room: floor, wall, wall-mounted indoor unit */}
      <Solid hatch={ids.hatch} x={16} y={224} width={448} height={14} edge="top" />
      <Solid hatch={ids.hatch} x={440} y={34} width={24} height={190} edge="left" />
      <rect className="hbB-card" x="322" y="50" width="110" height="34" rx="6" />
      <line className="hbB-thin" x1="330" y1="72" x2="424" y2="72" />
      <line className="hbB-thin" x1="330" y1="78" x2="424" y2="78" />
      <text className="hbB-soft" x="312" y="72" textAnchor="end">
        {en ? 'indoor unit' : 'vnitřní jednotka'}
      </text>

      {/* step 1 — leak sinks and pools at the floor, no flame nearby */}
      <On step="1">
        <line
          className="hbB-dim"
          x1="352"
          y1="92"
          x2="352"
          y2="178"
          markerEnd={`url(#${ids.arrow})`}
        />
        <line
          className="hbB-dim"
          x1="377"
          y1="92"
          x2="377"
          y2="186"
          markerEnd={`url(#${ids.arrow})`}
        />
        <line
          className="hbB-dim"
          x1="402"
          y1="92"
          x2="402"
          y2="178"
          markerEnd={`url(#${ids.arrow})`}
        />
        <text className="hbB-dim-txt" x="340" y="140" textAnchor="end">
          {en ? 'heavier than air' : 'těžší než vzduch'}
        </text>
        <rect className="hbB-no-soft" x="16" y="190" width="424" height="34" />
        <line className="hbB-ghost" x1="16" y1="190" x2="440" y2="190" />
        <text className="hbB-no-txt" x="24" y="213">
          {en ? 'odourless · displaces O₂' : 'bez zápachu · vytěsňuje O₂'}
        </text>
        <circle className="hbB-card" cx="110" cy="120" r="26" />
        <path
          className="hbB-flame"
          d="M110 134 c-10 -8 -13 -20 -5 -30 c2 6 6 9 6 9 c-2 -12 6 -18 11 -24 c0 10 6 14 8 20 c4 12 -6 22 -20 25 z"
        />
        <circle className="hbB-pict-red" cx="110" cy="120" r="26" />
        <path className="hbB-pict-red" d="M92 102 L128 138" />
        <text className="hbB-soft" x="110" y="168" textAnchor="middle">
          {en ? 'no open flame' : 'žádný plamen'}
        </text>
        <Verdict kind="no" x={416} y={207} r={14} />
      </On>

      {/* step 2 — certified recovery: unit → recovery machine → service cylinder */}
      <On step="2">
        <path className="hbB-draw hbA-pour" d="M330 84 C330 120 345 130 345 162" />
        <rect className="hbB-card" x="300" y="162" width="90" height="62" rx="4" />
        <circle className="hbB-thin" cx="322" cy="180" r="8" />
        <circle className="hbB-thin" cx="368" cy="180" r="8" />
        {/* name plate on the device itself — the hose above stays free of text */}
        <text className="hbB-lbl" x="345" y="214" textAnchor="middle">
          {en ? 'recovery unit' : 'odsávačka'}
        </text>
        <path className="hbB-draw hbA-pour" d="M300 196 H262" />
        <rect className="hbB-cell" x="222" y="148" width="40" height="76" rx="8" />
        <rect className="hbB-ink" x="236" y="138" width="12" height="10" />
        <text className="hbB-lbl" x="242" y="130" textAnchor="middle">
          {en ? 'service cylinder' : 'servisní láhev'}
        </text>
        <Verdict kind="ok" x={110} y={120} r={22} />
        <text className="hbB-ok-txt" x="110" y="162" textAnchor="middle">
          {en ? 'certified person' : 'certifikovaná osoba'}
        </text>
        <text className="hbB-soft" x="110" y="184" textAnchor="middle">
          (EU) 2024/573
        </text>
      </On>
    </Blueprint>
  );
}

/** Propane torch with its flame; `rot` is the flame direction in degrees (0 = +x). */
function Torch({ x, y, rot }: { x: number; y: number; rot: number }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rot})`}>
      <path className="hbB-thin" d="M-46 0 c-20 0 -30 -12 -44 -8" />
      <rect className="hbB-ink" x="-46" y="-5" width="40" height="10" rx="3" />
      <rect className="hbB-ink-soft" x="-8" y="-3" width="8" height="6" />
      <g className="hbA-spark">
        <path className="hbB-flame" d="M0 0 c8 -9 24 -9 34 0 c-10 9 -26 9 -34 0 z" />
        <path className="hbB-bolt" d="M6 0 c5 -4 12 -4 18 0 c-6 4 -13 4 -18 0 z" />
      </g>
    </g>
  );
}

/** Bitumen torching, roof-edge detail: flame into the parapet joint ignites hidden EPS ✗ vs. torch on the open membrane, hot air at the detail, extinguisher on the roof, watch ≥ 8 h ✓. */
export function BitumenTorchScene({ en }: { en: boolean }) {
  const ids = blueprintIds('bitumenTorch');
  return (
    <Blueprint
      id="bitumenTorch"
      label={
        en
          ? 'Bitumen torching: flame into the joint vs. flame only on the open membrane'
          : 'Natavování: plamen do spáry vs. plamen jen na volnou plochu'
      }
      title={en ? 'Torching · parapet detail' : 'Natavování · detail u atiky'}
      source={
        en ? 'Decree 87/2000 Coll. §3 · fire watch ≥ 8 h' : 'vyhl. 87/2000 Sb. §3 · dohled ≥ 8 h'
      }
    >
      {/* roof build-up: deck, EPS insulation, membrane, parapet with the joint */}
      <Solid hatch={ids.hatch} x={16} y={190} width={324} height={20} edge="top" />
      <rect className="hbB-cell" x="16" y="170" width="318" height="20" />
      <text className="hbB-soft" x="24" y="185">
        {en ? 'EPS insulation' : 'EPS izolace'}
      </text>
      <line className="hbB-draw-heavy" x1="16" y1="170" x2="332" y2="170" />
      {/* right of the step-2 torch, left of the hot-air gun — clear of the extinguisher */}
      <text className="hbB-soft" x="178" y="165">
        {en ? 'bitumen membrane' : 'asfaltový pás'}
      </text>
      <Solid hatch={ids.hatch} x={340} y={90} width={32} height={120} edge="left" />
      <text className="hbB-soft" x="356" y="80" textAnchor="middle">
        {en ? 'parapet' : 'atika'}
      </text>

      {/* step 1 — flame led into the joint; insulation smoulders unseen */}
      <On step="1">
        <Torch x={312} y={146} rot={48} />
        <circle className="hbB-warn-fill" cx="322" cy="181" r="7" />
        <circle className="hbB-ghost" cx="322" cy="181" r="14" />
        <g className="hbA-waft">
          <path className="hbB-thin" d="M336 168 c4 -10 -4 -16 0 -26" />
          <path className="hbB-thin" d="M341 168 c4 -12 -3 -20 1 -30" />
        </g>
        <text className="hbB-no-txt" x="200" y="96" textAnchor="middle">
          {en ? 'flame into the joint' : 'plamen do spáry'}
        </text>
        <text className="hbB-soft" x="16" y="234">
          {en ? 'insulation smoulders unseen' : 'izolace doutná skrytě'}
        </text>
        <Verdict kind="no" x={420} y={150} r={20} />
      </On>

      {/* step 2 — torch only on the open membrane, hot air at the detail, extinguisher at hand, watch afterwards */}
      <On step="2">
        <rect className="hbB-no-fill" x="70" y="122" width="20" height="46" rx="6" />
        <rect className="hbB-ink" x="76" y="112" width="8" height="10" />
        <path className="hbB-thin" d="M84 116 q14 -8 20 12" />
        <text className="hbB-soft" x="80" y="100" textAnchor="middle">
          {en ? 'extinguisher' : 'hasicí přístroj'}
        </text>
        <Torch x={160} y={138} rot={90} />
        <circle className="hbB-card" cx="240" cy="130" r="20" />
        <line className="hbB-draw hbA-clock-hand" x1="240" y1="130" x2="240" y2="116" />
        <circle className="hbB-ink" cx="240" cy="130" r="2" />
        <text className="hbB-dim-txt" x="240" y="98" textAnchor="middle">
          {en ? 'fire watch ≥ 8 h' : 'dohled ≥ 8 h'}
        </text>
        <rect className="hbB-ink" x="296" y="150" width="30" height="10" rx="3" />
        <rect className="hbB-ink-soft" x="326" y="152" width="8" height="6" />
        <path className="hbB-thin" d="M336 152 q3 3 0 6 M340 150 q4 5 0 10" />
        <text className="hbB-ok-txt" x="16" y="234">
          {en ? 'open membrane only' : 'jen volná plocha'}
        </text>
        <text className="hbB-ok-txt" x="464" y="234" textAnchor="end">
          {en ? 'details: hot air' : 'detail: horký vzduch'}
        </text>
        <Verdict kind="ok" x={420} y={150} r={20} />
      </On>
    </Blueprint>
  );
}

/** Gauge needle from the gauge centre (400,90) to angle `deg` (screen degrees, 0 = +x, clockwise). */
function Needle({ deg }: { deg: number }) {
  const rad = (deg * Math.PI) / 180;
  const x = 400 + 30 * Math.cos(rad);
  const y = 90 + 30 * Math.sin(rad);
  return (
    <g>
      <line className="hbB-draw-heavy" x1="400" y1="90" x2={x.toFixed(1)} y2={y.toFixed(1)} />
      <circle className="hbB-ink" cx="400" cy="90" r="4" />
    </g>
  );
}

/** Pressure relief: the safety valve vents the over-pressure and the vessel holds ✓ vs. a wedged valve, needle deep in the red zone, ruptured shell ✗. */
export function PressureReliefScene({ en }: { en: boolean }) {
  const ids = blueprintIds('pressureRelief');
  return (
    <Blueprint
      id="pressureRelief"
      label={
        en
          ? 'Pressure vessel: safety valve relieves vs. wedged valve'
          : 'Tlaková nádoba: pojistný ventil odpouští vs. podložený ventil'
      }
      title={en ? 'Safety valve · gauge' : 'Pojistný ventil · manometr'}
      source={
        en ? 'Gov. Reg. 192/2022 Coll. · Act 250/2021 Coll.' : 'NV 192/2022 Sb. · z. 250/2021 Sb.'
      }
    >
      {/* floor, vessel on legs, safety valve, pipe to the gauge */}
      <Solid hatch={ids.hatch} x={16} y={226} width={448} height={12} edge="top" />
      <line className="hbB-draw" x1="100" y1="206" x2="100" y2="226" />
      <line className="hbB-draw" x1="220" y1="206" x2="220" y2="226" />
      <rect className="hbB-cell" x="60" y="124" width="200" height="84" rx="42" />
      <rect className="hbB-draw" x="60" y="124" width="200" height="84" rx="42" />
      <text className="hbB-lbl" x="160" y="172" textAnchor="middle">
        {en ? 'pressure vessel' : 'tlaková nádoba'}
      </text>
      <rect className="hbB-ink" x="152" y="104" width="16" height="20" />
      <rect className="hbB-card" x="142" y="90" width="36" height="16" rx="3" />
      <path className="hbB-draw" d="M142 98 H124 V84" />
      <line className="hbB-draw" x1="178" y1="94" x2="206" y2="80" />
      <text className="hbB-lbl" x="160" y="66" textAnchor="middle">
        {en ? 'safety valve' : 'pojistný ventil'}
      </text>
      <path className="hbB-draw" d="M258 166 H352 V90 H360" />
      {/* gauge: green scale, red zone at the top-right end */}
      <circle className="hbB-card" cx="400" cy="90" r="40" />
      <circle className="hbB-draw" cx="400" cy="90" r="40" />
      <path className="hbB-ok" d="M376 114 A34 34 0 1 1 432.8 81.2" />
      <path className="hbB-no" d="M432.8 81.2 A34 34 0 0 1 424 114" />
      <path className="hbB-leader" d="M434 106 L446 200" />
      <text className="hbB-no-txt" x="464" y="214" textAnchor="end">
        {en ? 'red zone' : 'červené pole'}
      </text>

      {/* step 1 — needle at the limit, valve blows off, vessel holds */}
      <On step="1">
        <Needle deg={343} />
        <g className="hbA-waft">
          <line
            className="hbB-dim"
            x1="124"
            y1="80"
            x2="124"
            y2="52"
            markerEnd={`url(#${ids.arrow})`}
          />
          <path className="hbB-thin" d="M116 76 l-8 -10 M132 76 l8 -10" />
        </g>
        <text className="hbB-ok-txt" x="124" y="44" textAnchor="middle">
          {en ? 'vents the excess' : 'odpouští přetlak'}
        </text>
        <Verdict kind="ok" x={316} y={120} r={18} />
        <text className="hbB-ok-txt" x="316" y="152" textAnchor="middle">
          {en ? 'vessel holds' : 'nádoba drží'}
        </text>
      </On>

      {/* step 2 — wedge under the lever, needle deep in the red, shell ruptures */}
      <On step="2">
        <Needle deg={35} />
        <path className="hbB-no-fill" d="M180 100 l22 -12 v12 z" />
        <text className="hbB-no-txt" x="218" y="108">
          {en ? 'wedged' : 'podložený'}
        </text>
        <path className="hbB-no" d="M250 132 l-10 14 l12 10 l-10 14 l10 12" />
        <path className="hbB-no" d="M262 128 l12 -10 M266 150 h14 M262 176 l12 10" />
        <Verdict kind="no" x={316} y={120} r={18} />
        <text className="hbB-no-txt" x="330" y="196" textAnchor="middle">
          {en ? 'vessel ruptures' : 'roztržení nádoby'}
        </text>
      </On>
    </Blueprint>
  );
}
